import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import SiteGate from "./components/SiteGate";
import { TranslationProvider } from "./context/TranslationContext";
import TranslationBanner from "./components/TranslationBanner";
import Layout from "./components/Layout";
import Home from "./pages/Home";
import About from "./pages/About";
import Events from "./pages/Events";
import Membership from "./pages/Membership";
import Donate from "./pages/Donate";
import Contact from "./pages/Contact";
import Constitution from "./pages/Constitution";
import Reformers from "./pages/Reformers";
import Dhamma from "./pages/Dhamma";
import ChildrensCorner from "./pages/ChildrensCorner";
import ReadingBooklet from "./pages/ReadingBooklet";
import Education from "./pages/Education";
import EducationSample from "./pages/EducationSample";
import Ks2BookViewer from "./pages/Ks2BookViewer";
import Ks2WorkbookViewer from "./pages/Ks2WorkbookViewer";
import Ks2TeacherGuide from "./pages/Ks2TeacherGuide";
import FoundationBookViewer from "./pages/FoundationBookViewer";
import Ks1BookViewer from "./pages/Ks1BookViewer";
import Ks3BookViewer from "./pages/Ks3BookViewer";
import Ks4BookViewer from "./pages/Ks4BookViewer";
import KarunaFund from "./pages/KarunaFund";
import MeetingSignIn from "./pages/MeetingSignIn";
import GuestJoin from "./pages/GuestJoin";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import Admin from "./pages/Admin";
import PaymentReturn from "./pages/PaymentReturn";
import AuthCallback from "./pages/AuthCallback";
import PosterA3 from "./pages/PosterA3";
import PosterSocial from "./pages/PosterSocial";
import PosterMission from "./pages/PosterMission";
import PosterMissionSquare from "./pages/PosterMissionSquare";
import OurJourney from "./pages/OurJourney";
import CioChecklist from "./pages/admin/CioChecklist";
import Launch from "./pages/Launch";
import Profile from "./pages/Profile";
import MembershipCard from "./pages/MembershipCard";
import Verify from "./pages/Verify";
import Newsletter from "./pages/Newsletter";
import NewsletterIssue from "./pages/NewsletterIssue";
import AdminHelp from "./pages/AdminHelp";
import PublicBenefit from "./pages/PublicBenefit";
import Safeguarding from "./pages/Safeguarding";
import EqualityDiversity from "./pages/EqualityDiversity";
import MissionVision from "./pages/MissionVision";
import Volunteer from "./pages/Volunteer";
import Impact from "./pages/Impact";
import Transparency from "./pages/Transparency";
import Governance from "./pages/Governance";
import ConflictOfInterest from "./pages/ConflictOfInterest";
import CodeOfConduct from "./pages/CodeOfConduct";
import Privacy from "./pages/Privacy";
import Complaints from "./pages/Complaints";
import CookieConsent from "./components/CookieConsent";
import PlatformLayout from "./components/PlatformLayout";
import PlatformHome from "./pages/platform/PlatformHome";
import { MeetingLobby, MeetingRoom } from "./pages/platform/Meetings";
import PlatformLive from "./pages/platform/PlatformLive";
import PlatformLibrary from "./pages/platform/PlatformLibrary";
import PlatformMeditation from "./pages/platform/PlatformMeditation";
import PlatformAssistant from "./pages/platform/PlatformAssistant";
import { CommunityHome, CommunityThread, MemberDirectory } from "./pages/platform/Community";
import MemberProfile, { MemberProfileEdit } from "./pages/platform/MemberProfile";
import PlatformCalendar from "./pages/platform/PlatformCalendar";
import MeetingNotes from "./pages/platform/MeetingNotes";
import PlatformLearning from "./pages/platform/PlatformLearning";
import PlatformLearningModule from "./pages/platform/PlatformLearningModule";
import { ReadingCirclesHome, ReadingCircleDetail } from "./pages/platform/ReadingCircles";
import { ElectionsHome, ElectionDetail } from "./pages/platform/Elections";
import Grants from "./pages/Grants";
import ComingSoon from "./pages/platform/ComingSoon";

function ProtectedRoute({ children, adminOnly = false }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-[60vh] flex items-center justify-center text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  // Synchronous check for Google Auth callback (before ProtectedRoute)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  // Poster routes — render without Layout for pixel-perfect capture
  if (location.pathname.startsWith("/poster/")) {
    return (
      <Routes>
        <Route path="/poster/a3" element={<PosterA3 />} />
        <Route path="/poster/social" element={<PosterSocial />} />
        <Route path="/poster/mission" element={<PosterMission />} />
        <Route path="/poster/mission-square" element={<PosterMissionSquare />} />
      </Routes>
    );
  }
  // Guest meeting join — public, no Layout chrome (clean dedicated page)
  if (location.pathname === "/join" || location.pathname.startsWith("/join/")) {
    return (
      <Routes>
        <Route path="/join" element={<GuestJoin />} />
      </Routes>
    );
  }
  // Platform routes use their own (sidebar) layout — render without the public Layout chrome
  if (location.pathname.startsWith("/platform")) {
    return (
      <Routes>
        <Route element={<PlatformLayout />}>
          <Route path="/platform" element={<PlatformHome />} />
          <Route path="/platform/meetings" element={<MeetingLobby />} />
          <Route path="/platform/meetings/room/:roomName" element={<MeetingRoom />} />
          <Route path="/platform/live" element={<PlatformLive />} />
          <Route path="/platform/library" element={<PlatformLibrary />} />
          <Route path="/platform/meditation" element={<PlatformMeditation />} />
          <Route path="/platform/assistant" element={<PlatformAssistant />} />
          <Route path="/platform/community" element={<CommunityHome />} />
          <Route path="/platform/community/members" element={<MemberDirectory />} />
          <Route path="/platform/community/:threadId" element={<CommunityThread />} />
          <Route path="/platform/profile/edit" element={<MemberProfileEdit />} />
          <Route path="/platform/profile/:membershipId" element={<MemberProfile />} />
          <Route path="/platform/calendar" element={<PlatformCalendar />} />
          <Route path="/platform/notes" element={<MeetingNotes />} />
          <Route path="/platform/learning" element={<PlatformLearning />} />
          <Route path="/platform/learning/:moduleId" element={<PlatformLearningModule />} />
          <Route path="/platform/reading-circles" element={<ReadingCirclesHome />} />
          <Route path="/platform/reading-circles/:circleId" element={<ReadingCircleDetail />} />
          <Route path="/platform/elections" element={<ElectionsHome />} />
          <Route path="/platform/elections/:electionId" element={<ElectionDetail />} />
          <Route
            path="/platform/community-old"
            element={<ComingSoon
              title="Community"
              description="Discussion groups, regional chapters, youth and women's wings, all in one place."
              features={["Member directory with verified IDs", "Regional chapter pages", "Youth & women's wings", "Discussion threads with moderation"]}
            />}
          />
          <Route
            path="/platform/donations"
            element={<ComingSoon
              title="Donations"
              description="Support the Sangha, recurring donations, campaign fundraisers, dāna for visiting bhikkhus."
              features={["One-off and monthly via Stripe", "Campaign-specific giving", "Public donor wall (opt-in)", "Gift Aid: pending charity registration"]}
            />}
          />
          <Route
            path="/platform/settings"
            element={<ComingSoon
              title="Settings"
              description="Manage your profile, language, notifications and meeting preferences."
              features={["Edit profile and photo", "Default meeting language", "Email + push notifications", "Privacy controls"]}
            />}
          />
          <Route path="/platform/*" element={<Navigate to="/platform" replace />} />
        </Route>
      </Routes>
    );
  }

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/events" element={<Events />} />
        <Route path="/membership" element={<Membership />} />
        <Route path="/donate" element={<Donate />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/constitution" element={<Constitution />} />
        <Route path="/grants" element={<Grants />} />
        <Route path="/reformers" element={<Reformers />} />
        <Route path="/dhamma" element={<Dhamma />} />
        <Route path="/children" element={<ChildrensCorner />} />
        <Route path="/children/booklet" element={<ReadingBooklet />} />
        <Route path="/education" element={<Education />} />
        <Route path="/education/sample" element={<EducationSample />} />
        <Route path="/education/ks2-book" element={<Ks2BookViewer />} />
        <Route path="/education/ks2-workbook" element={<Ks2WorkbookViewer />} />
        <Route path="/education/ks2-teacher-guide" element={<Ks2TeacherGuide />} />
        <Route path="/education/foundation-book" element={<FoundationBookViewer />} />
        <Route path="/education/ks1-book" element={<Ks1BookViewer />} />
        <Route path="/education/ks3-book" element={<Ks3BookViewer />} />
        <Route path="/education/ks4-book" element={<Ks4BookViewer />} />
        <Route path="/karuna-fund" element={<KarunaFund />} />
        <Route path="/meeting/:meeting_id/signin" element={<MeetingSignIn />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/payment/return" element={<PaymentReturn />} />
        <Route path="/payment/cancel" element={<Donate />} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute adminOnly><Admin /></ProtectedRoute>} />
        <Route path="/launch" element={<Launch />} />
        <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
        <Route path="/profile/card" element={<ProtectedRoute><MembershipCard /></ProtectedRoute>} />
        <Route path="/verify/:membershipId" element={<Verify />} />
        <Route path="/newsletter" element={<Newsletter />} />
        <Route path="/newsletter/:slug" element={<NewsletterIssue />} />
        <Route path="/admin/help" element={<ProtectedRoute adminOnly><AdminHelp /></ProtectedRoute>} />
        <Route path="/admin/cio-checklist" element={<ProtectedRoute adminOnly><CioChecklist /></ProtectedRoute>} />
        <Route path="/mission" element={<MissionVision />} />
        <Route path="/public-benefit" element={<PublicBenefit />} />
        <Route path="/safeguarding" element={<Safeguarding />} />
        <Route path="/equality-diversity" element={<EqualityDiversity />} />
        <Route path="/volunteer" element={<Volunteer />} />
        <Route path="/impact" element={<Impact />} />
        <Route path="/transparency" element={<Transparency />} />
        <Route path="/governance" element={<Governance />} />
        <Route path="/conflict-of-interest" element={<ConflictOfInterest />} />
        <Route path="/code-of-conduct" element={<CodeOfConduct />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/complaints" element={<Complaints />} />
        <Route path="/our-journey" element={<OurJourney />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <TranslationProvider>
          <SiteGate>
            <Toaster position="top-center" richColors closeButton expand duration={5000} toastOptions={{ style: { fontSize: "14px" } }} />
            <TranslationBanner />
            <AppRouter />
            <CookieConsent />
          </SiteGate>
        </TranslationProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
